/**
 * Intelligent result merger for segment-based dream analysis using quality-gated RRF
 *
 * IMPLEMENTATION: Quality-Gated Reciprocal Rank Fusion (RRF)
 *
 * This merger implements a sophisticated fusion strategy that balances:
 * - Multi-segment coverage (rules matching multiple dream parts)
 * - Match quality (high cosine similarity scores)
 * - Adaptive thresholding (relative to best matches in query)
 *
 * ALGORITHM:
 * 1. Normalize all similarity scores relative to max similarity in query
 * 2. Apply quality-weighted RRF: score = (1/(k+rank+1)) * normalizedSimilarity
 * 3. Aggregate scores across all segment rankings
 * 4. Post-filter: remove rules where best segment < 70% of max similarity
 *
 * WHY POST-FILTERING:
 * Pre-filtering would prevent rules with mixed-quality segments (e.g., 70% + 40%)
 * from benefiting from multi-segment RRF boost. Post-filtering allows RRF to
 * accumulate scores, then removes rules where ALL segments are weak.
 *
 * PARAMETERS:
 * - k=60: Standard RRF constant (parameter-free fusion)
 * - qualityThreshold=0.7: Rules must have ≥1 segment at 70% of max similarity
 *
 * PREVENTS:
 * - Weak multi-segment matches (45%, 48%) beating strong single matches (78%)
 * - Score clustering issues (RRF uses ranks, not raw cosine similarities)
 *
 * Handles deduplication and score aggregation across multiple dream segments
 */

import type { SearchResult } from '../types/index.js';
import type { DreamSegment } from './dream-sentencizer';

export interface MergedResult {
  result: SearchResult;
  bestSegment: DreamSegment;
  allSegments: Array<{ segment: DreamSegment; score: number }>;
  aggregatedScore: number;
  occurrence_count: number;
}

export class ResultMerger {
  /**
   * Merge search results from multiple dream segments using RRF (Reciprocal Rank Fusion)
   * with quality-weighted scoring to balance breadth and precision
   * Deduplicates by rule ID and aggregates scores using rank-based fusion (k=60)
   */
  static mergeSegmentResults(
    segmentResults: Array<{ segment: DreamSegment; results: SearchResult[] }>
  ): MergedResult[] {
    console.log(`🔄 Merging results from ${segmentResults.length} segments using quality-weighted RRF...`);

    // Find max similarity across ALL segment results for normalization
    const allResults = segmentResults.flatMap(sr => sr.results);
    const maxSimilarity = Math.max(...allResults.map(r => r.similarity));

    console.log(`   Max similarity: ${maxSimilarity.toFixed(3)}, will post-filter at 0.7 normalized`);

    const k = 60; // Standard RRF constant
    const rrfScores = new Map<
      string,
      {
        score: number;
        bestResult: SearchResult;
        segments: Array<{ segment: DreamSegment; result: SearchResult; rank: number }>;
      }
    >();

    // For each segment, results are already ranked by cosine similarity
    for (const { segment, results } of segmentResults) {
      results.forEach((result, rank) => {
        const ruleId = (result.record as any).id;

        // Normalize similarity to 0-1 range based on max
        const normalizedSimilarity = result.similarity / maxSimilarity;

        // Quality-weighted RRF: multiply rank score by normalized similarity
        const baseRRF = 1 / (k + rank + 1); // rank is 0-indexed
        const rrfScore = baseRRF * normalizedSimilarity;

        if (!rrfScores.has(ruleId)) {
          rrfScores.set(ruleId, {
            score: 0,
            bestResult: result,
            segments: [],
          });
        }

        const entry = rrfScores.get(ruleId)!;
        entry.score += rrfScore;
        entry.segments.push({ segment, result, rank });

        // Track best result by similarity (for display)
        if (result.similarity > entry.bestResult.similarity) {
          entry.bestResult = result;
        }
      });
    }

    console.log(`   Found ${rrfScores.size} unique rules across all segments`);

    // Convert to MergedResult format and sort by RRF score
    const mergedResults = Array.from(rrfScores.entries()).map(([_ruleId, data]) => {
      const bestSegmentMatch = data.segments.reduce((best, curr) =>
        curr.result.similarity > best.result.similarity ? curr : best
      );

      return {
        result: data.bestResult,
        bestSegment: bestSegmentMatch.segment,
        allSegments: data.segments.map(s => ({
          segment: s.segment,
          score: s.result.similarity,
        })),
        aggregatedScore: data.score, // RRF score
        occurrence_count: data.segments.length,
      };
    });

    mergedResults.sort((a, b) => b.aggregatedScore - a.aggregatedScore);

    // Post-filter: remove rules where best segment is below 70% of max similarity
    const qualityThreshold = 0.7;
    const filteredResults = mergedResults.filter(result => {
      const bestSegmentSimilarity = Math.max(...result.allSegments.map(s => s.score));
      const normalizedBest = bestSegmentSimilarity / maxSimilarity;
      return normalizedBest >= qualityThreshold;
    });

    console.log(`   Merged into ${mergedResults.length} unique rules, ${filteredResults.length} after quality filter`);
    console.log(
      `   Top merged results (RRF):`,
      filteredResults
        .slice(0, 5)
        .map(
          m =>
            `${m.aggregatedScore.toFixed(4)} (${(m.result.record as any).id}, ${m.occurrence_count}x)`
        )
        .join(', ')
    );

    return filteredResults;
  }

  /**
   * Create empty result for error cases
   */
  static createEmptyMergedResults(): MergedResult[] {
    return [];
  }
}
